import { memo, ReactNode, useContext } from 'react';
import styled from '@emotion/styled';
import { zIndex } from 'presentations/views/styles';
import { gray04, white } from 'presentations/views/styles/color';
import WeekdayHeaderComp from './WeekdayHeader';
import MonthWeekdayHeaderComp from './MonthWeekdayHeader';
import AllDayTimeGridComp from './AllDayTimeGrid';
import MonthGridList from './MonthGridList';
import DayTimeGridList, { HorizontalScroller } from './DayTimeGridList';
import AllDayTimeGridRulerComp from './AllDayTimeGridRuler';
import WeekdayHeaderRulerComp from './WeekdayHeaderRuler';
import {
  MergeableEventListItem,
  Schedule,
} from 'presentations/views/components/atoms/Calendar/reducer';
import { CalendarContext } from 'presentations/views/components/atoms/Calendar/reducer/CalendarContext';

const Component = memo(() => {
  const { state } = useContext(CalendarContext);
  return state.showRange.month ? (
    <CalendarView>
      <Header>
        <MonthWeekdayHeaderView />
      </Header>
      <MonthGridList />
    </CalendarView>
  ) : (
    <CalendarView>
      <Header>
        <WeekdayHeaderView />
        <AllDayTimeGridView />
      </Header>
      <DayTimeGridList />
    </CalendarView>
  );
});

export const CalendarView = (props: {
  children: ReactNode;
  className?: string;
}) => {
  const { state } = useContext(CalendarContext);
  return (
    <Container className={props.className} ref={state.containerRef}>
      {props.children}
    </Container>
  );
};

export const WeekdayHeaderView = (props: {
  isHideRuler?: boolean;
  onClickAddTimeZone?: (el: HTMLElement) => void;
  onClickTimeZone?: (timeZone: string, el: HTMLElement) => void;
}) => {
  const { state } = useContext(CalendarContext);
  if (!state.isInit) return null;
  return (
    <WeekdayHeaderContainer>
      <WeekdayHeaderRulerComp
        onClickAddTimeZone={props.onClickAddTimeZone}
        onClickTimeZone={props.onClickTimeZone}
      />
      <HorizontalChildScroller
        scrollerWidth={state.containerWidth}
        rulerWidth={state.rulerWidth}
        ref={state.weekdayHeaderRef}
      >
        {state.pages.map((startedAt, page) => (
          <HorizontalScrollerItem key={page}>
            <WeekdayHeaderComp startedAt={startedAt} />
          </HorizontalScrollerItem>
        ))}
      </HorizontalChildScroller>
    </WeekdayHeaderContainer>
  );
};

export const MonthWeekdayHeaderView = () => {
  const { state } = useContext(CalendarContext);
  if (!state.isInit) return null;
  return (
    <WeekdayHeaderContainer>
      <MonthWeekdayHeaderComp />
    </WeekdayHeaderContainer>
  );
};

export const AllDayTimeGridView = (props: {
  onClickEvent?: (event: MergeableEventListItem, el: HTMLElement) => void;
  onUpdateSchedule?: (schedule: Schedule) => void;
  selectedEventId?: string;
}) => {
  const { state } = useContext(CalendarContext);
  if (!state.isInit) return null;
  return (
    <AllDayTimeGridContainer>
      <AllDayTimeGridRulerComp />
      <HorizontalChildScroller
        scrollerWidth={state.containerWidth}
        rulerWidth={state.rulerWidth}
        ref={state.allDayTimeGridRef}
      >
        {state.pages.map((startedAt, page) => (
          <HorizontalScrollerItem key={page}>
            <AllDayTimeGridComp
              startedAt={startedAt}
              onClickEvent={props.onClickEvent}
              selectedEventId={props.selectedEventId}
              onUpdateSchedule={props.onUpdateSchedule}
            />
          </HorizontalScrollerItem>
        ))}
      </HorizontalChildScroller>
    </AllDayTimeGridContainer>
  );
};

const Container = styled.div``;
const Header = styled.div`
  position: sticky;
  z-index: ${zIndex(1100)};
  top: 0;
  background-color: ${white};
  overflow-x: hidden;
`;

const WeekdayHeaderContainer = styled.div`
  display: flex;
  overflow-x: hidden;
`;
const AllDayTimeGridContainer = styled.div`
  display: flex;
  overflow-x: hidden;
  border-bottom: 2px solid ${gray04};
`;
const HorizontalChildScroller = styled(HorizontalScroller)`
  overflow: hidden;
`;
const HorizontalScrollerItem = styled.div`
  display: flex;
  width: inherit;
  /* will-change: transform, scroll-position; */
`;
export const WeekdayHeader = WeekdayHeaderComp;
export const AllDayTimeGrid = AllDayTimeGridComp;
export default Component;
